import { RefObject, useCallback, useEffect, useState } from "react";
import {
  DateRange,
  EventWidgetSize,
  EventWidgetSizeBreakPoint,
} from "../types";

export const useWidgetResizeObserver = (
  ref: RefObject<HTMLDivElement | null>,
  dateRange: DateRange,
) => {
  // The current size that should be used for the widget
  // - Mobile displays in a column with a portait card
  // - Table displays in a row with a wide landscape card
  // - Desktop display in a row with narrows landscape cards
  //   (2 or 3 columsn of cards)
  const [widgetSize, setWidgetSize] = useState<EventWidgetSize>(
    EventWidgetSize.Mobile,
  );
  const [widgetHeaderSize, setWidgetHeaderSize] = useState<EventWidgetSize>(
    EventWidgetSize.Mobile,
  );

  // Observe the widget container div (the main <Flex> component)
  const setResizeObserver = useCallback(() => {
    if (ref.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        // When recurring events are not shown, the width of the widget is expanded by 260px
        const noRecurringEventsOffset =
          dateRange.forceGridView ||
          dateRange.showFutureEventNotice ||
          !dateRange.isFullMonth
            ? 260
            : 0;

        const width = entries[0].contentRect.width;
        setWidgetSize(
          width < EventWidgetSizeBreakPoint.Tablet - noRecurringEventsOffset
            ? EventWidgetSize.Mobile
            : width <
                EventWidgetSizeBreakPoint.DeskTop - noRecurringEventsOffset
              ? EventWidgetSize.Tablet
              : width <
                  EventWidgetSizeBreakPoint.Large - noRecurringEventsOffset
                ? EventWidgetSize.Desktop
                : EventWidgetSize.Large,
        );
        // For the header, we don't ever have the feature/recurring split, so we can just use the width
        setWidgetHeaderSize(
          width < EventWidgetSizeBreakPoint.Tablet
            ? EventWidgetSize.Mobile
            : width < EventWidgetSizeBreakPoint.DeskTop
              ? EventWidgetSize.Tablet
              : width < EventWidgetSizeBreakPoint.Large
                ? EventWidgetSize.Desktop
                : EventWidgetSize.Large,
        );
      });

      resizeObserver.observe(ref.current);

      return () => resizeObserver.disconnect();
    } else {
      setTimeout(() => setResizeObserver(), 10);
    }
  }, [dateRange, ref]);

  useEffect(() => {
    setResizeObserver();
  }, [setResizeObserver]);

  return {
    widgetSize,
    widgetHeaderSize,
  };
};
